import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Kendo controls
import { Button } from '@progress/kendo-react-buttons';
import { getSelectedState, Grid, GridColumn, GridNoRecords } from '@progress/kendo-react-grid';
import { Checkbox, TextArea } from "@progress/kendo-react-inputs";
// Kendo provides
import { getter } from "@progress/kendo-react-common";
// Action
import { BaseAction } from '../../../Store/Actions/RootAction';
import { checkAsyncStatus, exportCSV, transferStructuresAsync, transferStructuresResult } from "../../../Store/Actions/Pages/MainPage";
import { setActiveAll, setDisableAll } from '../../../Store/Actions/Common/ActionState';
// Class & Component & Funtion
import { Timer } from '../../Common/Timer';
import { customMessage, getCurrentDate, RedirectWithComponent } from '../../../Constants/Utils';
import { CreateParamTransferStructure } from '../../../Models/RequestModel/TransferStructuresRequestModel';
import { SelectedFiscalPeriodStateModel } from '../../../Models/StateModel/FiscalPeriodInfoStateModel';
import { AllActionId } from '../../../Models/StateModel/ActionStateModel';
// Resource
import Router from '../../../Resources/Router.json';
import Screen from "../../../Resources/Screen.json";

const DATA_ITEM_KEY = "Index";
const SELECTED_FIELD = "Selected";
const idGetter = getter(DATA_ITEM_KEY);
const timer = new Timer(10000);

export default function TapStripSubjectContent(props) {
    const fiscalPeriodModel = useSelector(state => state.MainPage.fiscalPeriodModel);
    const selectedFiscalPeriodInfo = useSelector(state => state.MainPage.selectedFiscalPeriodInfo);
    const actionState = useSelector(state => state.ActionState.actionState);

    const [asTypeItems, setAsTypeItems] = useState([]);
    const [selectedState, setSelectedState] = useState({});
    const [message, setMessage] = useState(null);
    const [approvalFlowStatus, setApprovalFlowStatus] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        // Dispose timer if component unmount
        return function cleanup() {
            timer.dispose();
        };
    }, [])

    useEffect(() => {
        if (!fiscalPeriodModel || !selectedFiscalPeriodInfo)
            return;

        setMessage(null);

        // Bindding TargetFinancialStatements
        let asTypes = fiscalPeriodModel.GetTargetFinancialStatementTypes(selectedFiscalPeriodInfo.FiscalPeriodIndex);
        let asTypeItems = asTypes.map((item) => {
            return {
                SystemAccount: item.DisplayText,
                Index: item.Value,
                Selected: true
            }
        });

        // Set default checked every state update
        let seletedItems = asTypeItems.map((item, index) => ({
            [index]: item.Selected
        }));

        setAsTypeItems(asTypeItems);
        setSelectedState(Object.assign({}, ...seletedItems));

    }, [fiscalPeriodModel, selectedFiscalPeriodInfo]);

    const onSelectionMasterAccountChange = useCallback(
        (event) => {
            const newSelectedState = getSelectedState({
                event,
                selectedState: selectedState,
                dataItemKey: DATA_ITEM_KEY,
            });
            setSelectedState(newSelectedState);
        },
        [selectedState]
    );

    const handleTransferStructures = () => {
        // Set value message before transfer
        setMessage(null);

        const callBack = (res) => {
            if (!res) {
                // Redirect error page if don't have response
                return RedirectWithComponent(Router.Error);
            }

            let result = new BaseAction(res);
            if (result.statusCode === 202) {
                // Start polling action CheckStatusAsync
                timer.start(checkAsync, result, true);
            }
            else if (result.statusCode === 400) {
                // Active controll and show message if an error occurs
                dispatch(setActiveAll());
                setMessage(getMessageAfterTransferAsync(result.data));
            }
        }
        if (checkInputBeforeRequest()) {
            // Call action transfer if input is valid
            dispatch(setDisableAll());
            setMessage(Screen.TransferMessage.Transferring);

            let targetValue = new SelectedFiscalPeriodStateModel({
                FiscalPeriodIndex: selectedFiscalPeriodInfo.FiscalPeriodIndex,
                AccTypeIndex: selectedFiscalPeriodInfo.AccTypeIndex,
                FsTypeIndex: getSelectedFsTypeIndex()
            });
            let request = CreateParamTransferStructure(fiscalPeriodModel, targetValue, approvalFlowStatus);
            let transferStructures = transferStructuresAsync(request, callBack);
            dispatch(transferStructures)
        }
    }

    const checkAsync = (transferModel) => {
        if (!transferModel) {
            // Redirect error page if don't have response
            return RedirectWithComponent(Router.Error);
        }

        const callBackCheckAsync = (res) => {
            if (!res) {
                //Redirect error page if don't have response
                return RedirectWithComponent(Router.Error);
            }

            let baseAction = new BaseAction(res);
            if (baseAction.data) {
                if (baseAction.statusCode === 200) {
                    // Dispose timer and call action TransferStructuresResult 
                    timer.dispose();
                    handleTransferResult(baseAction);
                }
                else if (baseAction.statusCode === 400) {
                    // Dispose timer, active controlls and show message if an error occurs
                    timer.dispose();
                    dispatch(setActiveAll());
                    let detailMessage = getMessageAfterTransferAsync(baseAction.data);
                    setMessage(detailMessage);
                }
            }
        }
        // Setup param and call action CheckStatusAsync
        let paramsCheckStatus = {};
        let transferModelData = transferModel?.data;
        if (transferModelData) {
            paramsCheckStatus = {
                AccessKey: transferModelData.AccessKey,
                RequestKey: transferModelData.RequestKey
            };
            const dispatchCheckStatus = checkAsyncStatus(paramsCheckStatus, callBackCheckAsync);
            dispatch(dispatchCheckStatus);
        }

    }

    const handleTransferResult = (checkAsyncModel) => {
        dispatch(setActiveAll());
        timer.dispose();

        const callBackTransferResult = (res) => {
            if (!res) {
                //Redirect error page if don't have response
                return RedirectWithComponent(Router.Error);
            }

            // Show message result
            let baseAction = new BaseAction(res);
            let messageResult = getMessageAfterTransferResult(baseAction.data);
            setMessage(messageResult);
        }

        // SetUp param and call action TransferValuesResult
        let result = checkAsyncModel.data;
        if (result) {
            let paramsTransferResult = {
                AccessKey: result.AccessKey,
                RequestKey: result.RequestKey
            }
            let actionTransferResult = transferStructuresResult(paramsTransferResult, callBackTransferResult);
            dispatch(actionTransferResult);
        }
    }

    // Event change ApprovalFlowStatus
    const changeApprovalFlowStatus = (event) => {
        setApprovalFlowStatus(event.target.value);
    }

    // Custom message transfer
    const getMessageAfterTransferAsync = (transferModel) => {
        let detailMessage = customMessage(transferModel);
        return Screen.TransferMessage.Transferring + "\n" + detailMessage;
    }

    // Custom message transfer result
    const getMessageAfterTransferResult = (transferResultModel) => {
        let resultMessage = Screen.TransferMessage.Transferring + "\n";
        if (transferResultModel?.ResultCode === "Success") {
            return resultMessage + Screen.TransferMessage.TransferSuccess;
        }
        return resultMessage + customMessage(transferResultModel);
    }

    // Event onClick button [出力]
    const onExportCSV = () => {
        setMessage(null);

        if (!checkInputBeforeRequest()) return;
        const callBack = (res) => {
            if (!res)
                return RedirectWithComponent(Router.Error);

            let dataFile = new BaseAction(res);
            if (dataFile && dataFile.statusCode === 200) {
                saveFile(dataFile.data);
            }
            else {
                setMessage(customMessage(dataFile.data));
            }
        }
        let targetValue = new SelectedFiscalPeriodStateModel({
            FiscalPeriodIndex: selectedFiscalPeriodInfo.FiscalPeriodIndex,
            AccTypeIndex: selectedFiscalPeriodInfo.AccTypeIndex,
            FsTypeIndex: getSelectedFsTypeIndex()
        });
        let request = CreateParamTransferStructure(fiscalPeriodModel, targetValue, approvalFlowStatus);
        let transferValues = exportCSV(request, callBack);
        dispatch(transferValues);
    }

    const saveFile = (blob) => {
        let today = new Date();
        let month = getCurrentDate(today.getMonth() + 1);
        let day = getCurrentDate(today.getDate());
        let hour = getCurrentDate(today.getHours());
        let minute = getCurrentDate(today.getMinutes());
        let fileName = Screen.TapStripSubjectContent.FileNameCSVTitle + today.getFullYear() + month + day + hour + minute + ".csv";
        if (window.navigator.msSaveOrOpenBlob) {
            //IE11 & Edge
            window.navigator.msSaveOrOpenBlob(new Blob([blob]), fileName);
        }
        else {
            //Other browsers
            const url = window.URL.createObjectURL(
                new Blob([blob]),
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                fileName
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        }
    }

    // Check select item before transfer
    const checkInputBeforeRequest = () => {
        let canExcute = true;
        if (Object.keys(selectedState).length === 0 || !checkSelectedtatements(selectedState)) {
            canExcute = false;
            if (!canExcute) {
                props.onShowErrMessageBox();
            }
        }
        return canExcute;
    }

    const checkSelectedtatements = (selectedState) => {
        for (let key in selectedState) {
            if (selectedState.hasOwnProperty(key)) {
                let value = selectedState[key];
                if (value) {
                    return true;
                }
            }
        }
        return false;
    }

    const getSelectedFsTypeIndex = () => {
        let fsSelectedMap = new Map(Object.entries(selectedState));
        let fsTypeSelected = asTypeItems.map((item) => {
            return fsSelectedMap.get(`${item.Index}`) ? item : null;
        }).filter((el) => {
            return el !== null;
        });

        return fsTypeSelected.map((item) => {
            return item.Index;
        });
    }

    return (
        <React.Fragment>
            <div className="blockTapContent">
                <p style={{ margin: "0px" }}>{Screen.TapStripSubjectContent.TitleTransferStructures}</p>
                <Grid style={{ minHeight: "123px" }}
                    scrollable="none"
                    data={asTypeItems.map((item) => ({
                        ...item,
                        [SELECTED_FIELD]: selectedState[idGetter(item)],
                    }))}
                    dataItemKey={DATA_ITEM_KEY}
                    selectedField={SELECTED_FIELD}
                    selectable={{
                        enabled: false,
                        drag: false,
                        cell: false,
                        mode: "multiple",
                    }}
                    onSelectionChange={onSelectionMasterAccountChange}
                    reorderable={true}>
                    <GridNoRecords>{Screen.TapStripAmountContent.TitleNoRecord}</GridNoRecords>
                    <GridColumn
                        field={SELECTED_FIELD}
                        width="50px"
                        className="tapAmountHeader"
                    />
                    <GridColumn field="SystemAccount" title={Screen.TapStripSubjectContent.SystemAccountTitle} />
                </Grid>
            </div>
            <div className="row" style={{ paddingTop: "9px" }}>
                <div className="col-md-6" style={{ display: "flex", alignItems: "center" }}>
                    <Checkbox disabled={actionState.IsDisable(AllActionId.ChkApprovalFlowStatus)}
                        defaultChecked={approvalFlowStatus}
                        checked={approvalFlowStatus}
                        label={Screen.TapStripAmountContent.ApprovalFlowStatus}
                        onChange={changeApprovalFlowStatus} />

                </div>
                <div className="col-md-6" style={{ display: "flex", justifyContent: "right" }}>
                    <Button className="btn pnxBtnPrimary btnSubmit" style={{ minWidth: "80px", marginRight: "10px" }}
                        onClick={onExportCSV}
                        disabled={actionState.IsDisable(AllActionId.BtnExportCSV)}> {Screen.TapStripSubjectContent.ButtonExport}
                    </Button>
                    <Button className="btn pnxBtnPrimary btnSubmit" style={{ minWidth: "80px" }}
                        onClick={handleTransferStructures}
                        disabled={actionState.IsDisable(AllActionId.BtnTransferStructure)}> {Screen.TapStripAmountContent.ButtonTransferValues}
                    </Button>
                </div>
            </div>
            <div className="row col-md-12" style={{ paddingTop: "10px", paddingLeft: "0px", paddingRight: "0px", marginLeft: "0px" }}>
                <TextArea rows={10} style={{ width: "100%", padding: "15px 15px 0px 0px" }} value={message ? message : ""} readOnly={true} />
            </div>
        </React.Fragment>
    )
}